// @flow
import $ from 'jquery'
import _ from 'lodash'
import React, { Component, Fragment } from 'react'
import Select from '../controls/Select'
import ExposedSelect from '../controls/ExposedSelect'
import Checkbox from '../controls/Checkbox'
import { translations, translateFunc } from '../../shared/localize'
import Datepicker from '../controls/Datepicker'
import styled, { type StyledComponent } from 'styled-components'
import type { OptionType } from '../types'
import xlsxIcon from './xlsx_icon.png'
import FilterFields from './FilterFields'
import type { GroupedHeaders } from '../controls/groupHeaders'

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  width: 600px;
`

const Row: StyledComponent<Object, any, any> = styled.div`
  padding-bottom: 6px;
`

export const RowLabel: StyledComponent<Object, any, any> = styled.div`
  padding: 3px 0;
  font-size: 12px;
  color: gray;
`

const StatisticsFilterFormDatepicker: any = styled(Datepicker)`
  height: 35px;
  width: 200px !important;

  &:disabled {
    color: #aaa;
  }
  font-size: 14px;
`

const Providers: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  padding-top: 5px;
`

const ProvidersItem: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  margin: 0 16px 8px 0;
  font-size: 14px;
`

const Buttons: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -8px;
`

const Button: StyledComponent<Object, any, any> = styled.button`
  display: inline-block;
  background: #fff;
  line-height: 30px;
  font-size: 14px;
  padding: 2px 12px 0;
  border: 1px solid #ccc;
  border-radius: 5px;
  text-decoration: none;
  color: black;
  margin-left: 8px;
  &:disabled {
    color: #aaa;
  }
  &:hover:not(:disabled) {
    background-color: #eee;
  }
  &:active {
    border: 1px solid #888;
  }
`

const ButtonImg: StyledComponent<Object, any, any> = styled.div`
  margin-right: 8px;
  width: 16px;
  height: 16px;
  background-image: url(${xlsxIcon});
  background-size: contain;
  position: relative;
  top: 3px;
  display: inline-block;
`

type Props = {|
  datepickerData: Object,
  applyDisabled?: boolean,
  disabled?: boolean,
  campaignId: number,
  onCampaignIdChange?: Function,
  campaignOptions: Array<OptionType>,
  audienceId: number,
  onAudienceIdChange?: Function,
  audienceOptions: Array<OptionType>,
  total: boolean,
  onTotalChange?: Function,
  groupByValue: string,
  groupByOptions: Array<OptionType>,
  onGroupByChange?: Function,
  providers: Array<string>,
  selectedProviders: Array<string>,
  onSelectedProvidersChange?: Function,
  datepickerValue: string,
  onDatepickerChange?: Function,
  onApply?: Function,
  downloadXlsxUrl?: string,
  selectedStatisticFields: Array<string>,
  statisticFields: GroupedHeaders,
  onSelectedStatisticFieldsChanged?: Function
|}

const t = translateFunc(translations.statistics)

export default class Form extends Component<Props> {
  datepicker: Datepicker
  render() {
    const {
      onDatepickerChange,
      datepickerData,
      disabled,
      campaignOptions,
      campaignId,
      audienceOptions,
      audienceId,
      groupByOptions,
      groupByValue,
      total,
      onTotalChange,
      providers,
      selectedProviders,
      onApply,
      applyDisabled,
      onCampaignIdChange,
      onAudienceIdChange,
      onGroupByChange,
      datepickerValue,
      selectedStatisticFields,
      statisticFields
    } = this.props

    return (
      <Fragment>
        <Wrapper>
          <Row>
            <RowLabel>{t('period')}</RowLabel>
            <StatisticsFilterFormDatepicker
              onChange={(value) => onDatepickerChange && onDatepickerChange(value)}
              data={{ ...datepickerData, initial: datepickerValue }}
              disabled={disabled}
            />
          </Row>
          <Row>
            <RowLabel>{t('campaigns')}</RowLabel>
            <Select
              options={campaignOptions}
              disabled={disabled}
              value={campaignId}
              onChange={(val) => onCampaignIdChange && onCampaignIdChange(val)}
            />
          </Row>
          <Row>
            <RowLabel>{t('audiences')}</RowLabel>
            <Select
              options={audienceOptions}
              value={audienceId}
              disabled={disabled}
              onChange={(val) => onAudienceIdChange && onAudienceIdChange(val)}
            />
          </Row>
          <Row>
            <RowLabel>{t('group_by')}</RowLabel>
            <ExposedSelect
              options={groupByOptions}
              disabled={disabled}
              value={groupByValue}
              onChange={(val) => onGroupByChange && onGroupByChange(val)}
            />
          </Row>
          <Row>
            <RowLabel>{t('providers')}</RowLabel>
            <Providers>
              <ProvidersItem>
                <Checkbox
                  checked={total}
                  liteDisableMode
                  disabled={disabled}
                  onChange={(checked) => onTotalChange && onTotalChange(checked)}
                  label={t('total')}
                />
              </ProvidersItem>
              {providers.map((provider) => (
                <ProvidersItem key={provider}>
                  <Checkbox
                    checked={_.includes(selectedProviders, provider)}
                    liteDisableMode
                    disabled={disabled}
                    onChange={(checked) => this.providerChanged(provider, checked)}
                    label={t(provider)}
                  />
                </ProvidersItem>
              ))}
            </Providers>
          </Row>
        </Wrapper>
        <Row>
          <FilterFields
            groupedFields={statisticFields}
            selectedFields={selectedStatisticFields}
            disabled={disabled}
            onChange={(field, checked) => this.statisticFieldsChanged(field, checked)}
          />
        </Row>
        <Wrapper>
          <Row>
            <RowLabel />
            <Buttons>
              <Button onClick={() => onApply && onApply()} disabled={applyDisabled || disabled}>
                {t('apply')}
              </Button>
              <Button disabled={disabled} onClick={() => this.downloadXlsx()}>
                <ButtonImg />
                {t('export_xlsx')}
              </Button>
            </Buttons>
          </Row>
        </Wrapper>
      </Fragment>
    )
  }

  providerChanged(provider: string, checked: boolean) {
    const { selectedProviders, onSelectedProvidersChange } = this.props
    let providers = this.checkboxData(selectedProviders, provider, checked)
    onSelectedProvidersChange && onSelectedProvidersChange(providers)
  }

  statisticFieldsChanged(field: string, checked: boolean) {
    const { selectedStatisticFields, onSelectedStatisticFieldsChanged } = this.props
    let fields = this.checkboxData(selectedStatisticFields, field, checked)
    onSelectedStatisticFieldsChanged && onSelectedStatisticFieldsChanged(fields)
  }

  checkboxData(collection: Array<string>, item: string, checked: boolean): Array<string> {
    if (checked) {
      return _.sortBy(_.uniq(_.concat([item], collection)))
    } else {
      return _.without(collection, item)
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.datepickerValue !== this.props.datepickerValue) {
      $(this.datepicker).val(nextProps.datepickerValue)
    }
  }

  downloadXlsx() {
    window.location.href = this.props.downloadXlsxUrl
  }
}
